import type { DoseStatus } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { useNow, useOnResume } from '../lib/fresh.js';
import { Link } from 'react-router-dom';
import { api, type DoseSlot, type MedicationsToday } from '../lib/api.js';
import {
  DoseFeelings,
  DoseSlotCard,
  TakenAtEditor,
  clockNow,
  formatClock,
  localDay,
  logSlotOptimistic,
} from './DoseSlots.js';
import { AlertIcon, CheckIcon, ClockIcon } from './Icons.js';

/**
 * Today's doses, on the Today screen.
 *
 * Medications lived behind a link in Settings, which for a daily-use feature is
 * the same as not existing — the failure mode this is for is forgetting, and a
 * screen you have to remember to open does not help with forgetting. The doses
 * belong where the person already is.
 *
 * Two requests rather than one because `medicationsToday` cannot tell "no
 * medications" from "none due today", and those want opposite things on screen:
 * an invitation to set one up, or a quiet line saying there is nothing on.
 *
 * Everything here fails quiet. Today is the first screen of the app and it opens
 * when things are bad; a medication request that times out must not turn it into
 * an error page. The one exception is a failed tap, which is reported, because
 * the user just did something and believes it worked.
 */
export function TodayDoses(): JSX.Element | null {
  const [today, setToday] = useState<MedicationsToday | null>(null);
  const [hasMedications, setHasMedications] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null = follow the day's state; true/false = the person overrode it.
  const [expanded, setExpanded] = useState<boolean | null>(null);
  // Which slot's details are open, as `medicationId@scheduledFor`. The editor
  // renders below the rail rather than inside a 19rem card.
  const [editing, setEditing] = useState<string | null>(null);

  /*
   * Fetched for the day it is, and again on coming back to the app (20 Oct
   * 2026). It fetched once on mount, so a Today left open overnight showed
   * yesterday's doses as today's, and a dose that came due while the phone was
   * in a pocket was not there when it came out.
   */
  const now = useNow();
  const calendarDay = localDay(now);
  const load = (): void => {
    void api
      .medicationsToday()
      .then(setToday)
      .catch(() => {});
    void api
      .listMedications(true)
      .then((meds) => setHasMedications(meds.length > 0))
      .catch(() => {});
  };
  useEffect(load, [calendarDay]);
  useOnResume(load);

  // Nothing at all until both answers are in: a section that appears a beat
  // after the rest of the page pushes everything below it down under the
  // reader's thumb.
  if (hasMedications === null || today === null) return null;

  function log(slot: DoseSlot, status: DoseStatus): void {
    if (!today) return;
    void logSlotOptimistic(today, slot, status, setToday, setError);
  }

  /*
   * NOTHING, WHEN THERE IS NOTHING DUE.
   *
   * This was an invitation to set medications up: a heading, a card and two
   * lines of prose, shown to anybody who had not. It cost about 110px at the
   * top of Today — above the check-in, which is the thing the screen is for —
   * and it pushed Save off a 375x812 screen by 28px, so the two gestures the
   * whole release is about became two gestures and a scroll.
   *
   * An empty state that advertises a feature is an advert. Settings and the
   * Medications screen are where somebody who wants this goes looking, and
   * neither of them is hard to find.
   */
  if (!hasMedications) return null;

  // The server builds slots medication by medication, so they arrive grouped by
  // drug rather than in the order the day happens.
  const slots = [...today.slots].sort((a, b) => a.time.localeCompare(b.time));
  // Only trustworthy when the phone and the account agree about what day it is;
  // a profile timezone set somewhere else would otherwise mark the morning dose
  // as still to come.
  const clockApplies = today.day === calendarDay;
  const nowClock = clockNow();

  /*
   * Open only when there is something to do about a dose right now.
   *
   * Two things close it, and they are different states of the same idea.
   *
   * Everything answered: the day is dealt with, and four cards you have already
   * tapped do not need the top of the screen.
   *
   * Nothing due yet: at half past midnight the calendar has rolled over but the
   * person has not — they are still finishing last night, and the first dose is
   * seven hours away. Presenting it as a live card asks a question that has no
   * answer yet. "Next at 8:00 AM" is the true and useful thing to say.
   *
   * What keeps it open is a dose whose time has come and which nobody has
   * answered — including one the app has assumed, since that is precisely what
   * the note asks them to check, and folding it away would be the app quietly
   * settling a question it had just promised to raise.
   *
   * All of it falls back to open when the phone and the account disagree about
   * what day it is, because then none of these clock comparisons mean anything.
   */
  const answered = slots.filter((s) => s.status !== null).length;
  const allAnswered = slots.length > 0 && answered === slots.length;
  const dueUnanswered = slots.filter((s) => s.status === null && s.time <= nowClock);
  const upcoming = slots.filter((s) => s.time > nowClock);
  const nothingToDo = clockApplies && slots.length > 0 && dueUnanswered.length === 0;
  const open = expanded ?? !nothingToDo;

  return (
    <section className="stack">
      <div className="row-between">
        <h2>Medication</h2>
        <Link className="btn btn-ghost btn-compact" to="/medications">
          All medications
        </Link>
      </div>

      {!open && (
        <button className="dose-summary" onClick={() => setExpanded(true)}>
          {/* A tick for a day that is done; a clock for one that has not started.
              A tick against nothing-taken-yet would be congratulating them for
              the time of day. */}
          {allAnswered ? <CheckIcon /> : <ClockIcon />}
          <span className="grow">
            {allAnswered
              ? slots.map((s) => s.medicationName).filter((n, i, a) => a.indexOf(n) === i).join(' · ')
              : `Next at ${formatClock(upcoming[0]!.time)}`}
          </span>
          {answered > 0 && (
            <span className="faint">
              {answered} of {slots.length}
            </span>
          )}
        </button>
      )}

      {open && (
        <>
      {/* The assumption has to be said out loud where the doses are. Silence
          would mean a number the app made up looks exactly like one the person
          confirmed, and this is the screen where that gets corrected. One short
          line and a mark to catch the eye — an ask, not a nag, and no red, no
          count, nothing to fail at. */}
      <p className="dose-note">
        <AlertIcon />
        <span>Counted as taken unless you say otherwise — please confirm.</span>
      </p>

      {error && <div className="banner banner-error">{error}</div>}

      {slots.length === 0 ? (
        <div className="card card-quiet">
          <p className="muted">
            Nothing scheduled for today. Anything you take can still be logged on the medications
            screen.
          </p>
        </div>
      ) : (
        // One or two doses stack at full width. A rail holding one item is a
        // narrower card with dead space beside it, and a rail holding two was
        // worse than that: it scrolled where it did not need to, and on a
        // desktop — where this app is still a ~544px column — it sliced the
        // second card down the middle of a word with the scrollbar hidden.
        //
        // The rail exists so a card stays wide enough for its three chips to sit
        // on one line. Stacked cards are full width, so they never had that
        // problem. From three on it earns its keep again and the section stays
        // one card tall however many medications there are.
        <div className={slots.length > 2 ? 'dose-rail' : 'stack-sm stack'}>
          {slots.map((slot) => (
            <DoseSlotCard
              key={`${slot.medicationId}-${slot.scheduledFor}`}
              slot={slot}
              clockApplies={clockApplies}
              nowClock={nowClock}
              onLog={log}
              compact={slots.length > 1}
              onExpand={() => setEditing(`${slot.medicationId}@${slot.scheduledFor}`)}
              day={today.day}
              onTimeChanged={() => {
                void api.medicationsToday().then(setToday).catch(() => {});
              }}
            />
          ))}
        </div>
      )}

      {/* Last night, still answerable.
          A dose due at 11pm was reachable for an hour and then gone: at midnight
          this screen rebuilt around a new day and took the slot with it. Nothing
          was lost from the record — it is assumed taken and counted either way —
          but there was nowhere left to confirm it, or to say it had actually been
          missed, and that correction is the whole reason for asking. Drops off
          after 4am; the Log keeps it editable after that. */}
      {today.carryOver.length > 0 && (
        <div className="stack-sm stack" style={{ marginTop: 'var(--space-4)' }}>
          <p className="faint">
            From last night, still unanswered. Assumed taken unless you say otherwise.
          </p>
          <div className={today.carryOver.length > 2 ? 'dose-rail' : 'stack-sm stack'}>
            {today.carryOver.map((slot) => (
              <DoseSlotCard
                key={`carry-${slot.medicationId}-${slot.scheduledFor}`}
                slot={slot}
                // Yesterday is over, so nothing here is "later today" and the
                // clock has nothing left to say about it.
                clockApplies={false}
                nowClock={nowClock}
                onLog={log}
                compact={today.carryOver.length > 1}
                onExpand={() => setEditing(`${slot.medicationId}@${slot.scheduledFor}`)}
                day={slot.scheduledFor.slice(0, 10)}
                onTimeChanged={() => {
                  void api.medicationsToday().then(setToday).catch(() => {});
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* The details for one dose, at full width.
          A rail card is 19rem and sized for three chips; ten feeling chips and
          a text field belong under it, not in it. */}
      {editing &&
        (() => {
          const slot = [...slots, ...today.carryOver].find(
          (s) => `${s.medicationId}@${s.scheduledFor}` === editing,
        );
          if (!slot) return null;
          const refresh = (): void => {
            void api.medicationsToday().then(setToday).catch(() => {});
          };
          return (
            <div className="card stack-sm stack">
              <div className="row-between">
                <strong>{slot.medicationName}</strong>
                <button className="btn btn-ghost btn-compact" onClick={() => setEditing(null)}>
                  Done
                </button>
              </div>
              <DoseFeelings slot={slot} onChanged={refresh} startOpen />
              {slot.takenAt && <TakenAtEditor slot={slot} day={today.day} onChanged={refresh} />}
            </div>
          );
        })()}

      {/* Only offered when there is something to fold away. Collapsing a
          section that is already asking you to confirm something would hide the
          ask. */}
      {nothingToDo && (
        <button className="btn btn-ghost btn-block" onClick={() => setExpanded(false)}>
          Hide
        </button>
      )}
        </>
      )}
    </section>
  );
}
